import React, { useState, useEffect, useCallback, useRef } from 'react';
import { clsx } from 'clsx';
import {
  HiAdjustmentsHorizontal,
  HiArrowsUpDown as HiArrowUpDown,
  HiChevronUp,
  HiChevronDown,
  HiEye,
  HiEyeSlash
} from 'react-icons/hi2';
import { TicketCard } from '@/components/TicketCard';
import { TicketRow } from '@/components/TicketRow';
import { TicketInboxRow } from '@/components/TicketInboxRow';
import { ViewToggle } from '@/components/ViewToggle';
import { BulkActionsBar } from '@/components/BulkActionsBar';
import { Button } from '@/components/Button';
import { Loading } from '@/components/Loading';
import type { 
  TicketListViewProps, 
  TicketViewType, 
  TicketViewPreferences 
} from '@/types/ui';

export const AVAILABLE_TABLE_COLUMNS = [
  { key: 'select', label: 'Select', required: true },
  { key: 'id', label: 'Ticket ID', sortable: true },
  { key: 'subject', label: 'Subject', sortable: true },
  { key: 'requester', label: 'Customer' },
  { key: 'company', label: 'Company' },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'priority', label: 'Priority', sortable: true },
  { key: 'assignedTo', label: 'Assigned To', sortable: true },
  { key: 'createdAt', label: 'Created', sortable: true },
  { key: 'updatedAt', label: 'Last Updated', sortable: true },
  { key: 'actions', label: 'Actions', required: true }
];

/**
 * Enhanced Ticket List View - Main container component
 * Orchestrates all three view layouts with professional styling
 */
export const TicketListView: React.FC<TicketListViewProps> = ({
  tickets,
  loading = false,
  selectedTickets = [],
  onTicketSelect,
  onTicketClick,
  onStatusChange,
  onPriorityChange,
  onAssignmentChange,
  onBulkStatusChange,
  onBulkPriorityChange,
  onBulkAssign,
  viewPreferences,
  onViewPreferencesChange,
  bulkActionsEnabled = true,
  virtualScrolling = false,
  showQuickActions = true,
  sortConfig,
  onSortChange,
  className,
  'data-testid': testId,
  ...props
}) => {
  // Default view preferences
  const defaultPreferences: TicketViewPreferences = {
    viewType: 'table',
    tableColumns: ['select', 'id', 'subject', 'requester', 'status', 'priority', 'assignedTo', 'updatedAt', 'actions'],
    cardSize: 'comfortable',
    tableRowHeight: 'comfortable',
    showAvatars: true,
    showPreviews: true
  };

  const [currentPreferences, setCurrentPreferences] = useState<TicketViewPreferences>(
    viewPreferences || defaultPreferences
  );

  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const columnSettingsRef = useRef<HTMLDivElement>(null);

  // Close column dropdown when clicking outside (BugHerd #112)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (columnSettingsRef.current && !columnSettingsRef.current.contains(event.target as Node)) {
        setShowColumnSettings(false);
      }
    };

    if (showColumnSettings) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [showColumnSettings]);

  // Update preferences when prop changes - preserve canonical order
  useEffect(() => {
    if (viewPreferences) {
      const enabledSet = new Set(viewPreferences.tableColumns);
      let columns = AVAILABLE_TABLE_COLUMNS
        .map(col => col.key)
        .filter(key => enabledSet.has(key));
      if (!columns.includes('actions')) {
        columns.push('actions');
      }

      setCurrentPreferences({
        ...viewPreferences,
        tableColumns: columns
      });
    }
  }, [viewPreferences]);

  // Handle view type change
  const handleViewChange = useCallback((viewType: TicketViewType) => {
    const newPreferences = { ...currentPreferences, viewType };
    setCurrentPreferences(newPreferences);
    onViewPreferencesChange?.(newPreferences);
  }, [currentPreferences, onViewPreferencesChange]);

  // Handle column visibility toggle - preserve canonical column order (BH#109)
  const handleColumnToggle = useCallback((column: string) => {
    const isCurrentlyVisible = currentPreferences.tableColumns.includes(column);
    const enabledSet = new Set(
      isCurrentlyVisible
        ? currentPreferences.tableColumns.filter(col => col !== column)
        : [...currentPreferences.tableColumns, column]
    );

    let newColumns = AVAILABLE_TABLE_COLUMNS
      .map(col => col.key)
      .filter(key => enabledSet.has(key));

    if (!newColumns.includes('actions')) {
      newColumns.push('actions');
    }

    const newPreferences = { ...currentPreferences, tableColumns: newColumns };
    setCurrentPreferences(newPreferences);
    onViewPreferencesChange?.(newPreferences);
  }, [currentPreferences, onViewPreferencesChange]);

  // Handle bulk selection
  const handleBulkSelect = useCallback((ticketIds: string[]) => {
    onTicketSelect?.(ticketIds);
  }, [onTicketSelect]);

  const handleSelectAll = useCallback(() => {
    const allIds = (tickets || []).map(ticket => ticket.id);
    handleBulkSelect(allIds);
  }, [tickets, handleBulkSelect]);

  const handleSelectNone = useCallback(() => {
    handleBulkSelect([]);
  }, [handleBulkSelect]);

  // Handle individual ticket selection
  const handleTicketToggle = useCallback((ticketId: string, selected: boolean) => {
    const newSelection = selected 
      ? [...selectedTickets, ticketId]
      : selectedTickets.filter(id => id !== ticketId);
    handleBulkSelect(newSelection);
  }, [selectedTickets, handleBulkSelect]);

  // The API sorts the complete result set before pagination. Re-sorting only
  // this page would make the visible order disagree with adjacent pages.
  const sortedTickets = tickets;

  // Available columns for table view
  const availableColumns = [
    { key: 'select', label: 'Select', required: true },
    { key: 'id', label: 'Ticket ID', sortable: true },
    { key: 'subject', label: 'Subject', sortable: true },
    { key: 'requester', label: 'Customer' },
    { key: 'company', label: 'Company' },
    { key: 'status', label: 'Status', sortable: true },
    { key: 'priority', label: 'Priority', sortable: true },
    { key: 'assignedTo', label: 'Assigned To', sortable: true },
    { key: 'createdAt', label: 'Created', sortable: true },
    { key: 'updatedAt', label: 'Last Updated', sortable: true },
    { key: 'actions', label: 'Actions', required: true }
  ];

  // Handle sorting
  const handleSort = (key: string) => {
    // Map frontend column keys to backend sort fields
    const sortKeyMap: Record<string, string> = {
      'id': 'ticketnumber',
      'ticketnumber': 'ticketnumber',
      'subject': 'subject',
      'status': 'status',
      'priority': 'priority',
      'updatedAt': 'updatedat',
      'createdAt': 'createdat',
      'assignedTo': 'assignedto' // Sort by assigned user
    };

    const backendKey = sortKeyMap[key] || key;
    onSortChange?.(backendKey);
  };

  // Helper function to check if a column is currently sorted
  const isColumnSorted = (columnKey: string) => {
    // Map frontend column keys to backend sort fields for comparison
    const sortKeyMap: Record<string, string> = {
      'id': 'ticketnumber',
      'ticketnumber': 'ticketnumber',
      'subject': 'subject',
      'status': 'status',
      'priority': 'priority',
      'updatedAt': 'updatedat',
      'createdAt': 'createdat',
      'assignedTo': 'assignedto' // Sort by assigned user
    };

    const backendKey = sortKeyMap[columnKey] || columnKey;
    return sortConfig?.key === backendKey;
  };

  // Helper function to get the display label for the currently sorted column
  const getSortedColumnLabel = () => {
    // Reverse map backend sort fields to frontend column keys
    const backendToFrontendMap: Record<string, string> = {
      'ticketnumber': 'id',
      'subject': 'subject',
      'status': 'status',
      'priority': 'priority',
      'updatedat': 'updatedAt',
      'createdat': 'createdAt'
    };

    const sortKey = sortConfig?.key;
    if (!sortKey) return '';
    const frontendKey = backendToFrontendMap[sortKey] || sortKey;
    const column = availableColumns.find(col => col.key === frontendKey);
    return column?.label || sortKey;
  };

  // Render table header
  const renderTableHeader = () => {
    return (
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr>
          {currentPreferences.tableColumns.map((columnKey) => {
            const column = availableColumns.find(col => col.key === columnKey);
            if (!column) return null;

            const isSorted = isColumnSorted(columnKey);
            const canSort = column.sortable;

            return (
              <th
                key={columnKey}
                className={clsx(
                  'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
                  canSort && !loading && 'cursor-pointer hover:bg-gray-100 select-none',
                  canSort && loading && 'cursor-not-allowed opacity-50',
                  // Ensure actions column has proper width and doesn't get squeezed, and is always visible
                  columnKey === 'actions' && 'w-20 min-w-20 sticky right-0 bg-gray-50 z-10',
                  // Ensure other critical columns maintain their widths
                  columnKey === 'select' && 'w-12 min-w-12',
                  columnKey === 'id' && 'w-40 min-w-40',
                  columnKey === 'ticketnumber' && 'w-40 min-w-40',
                  columnKey === 'requester' && 'w-48 min-w-48',
                  columnKey === 'company' && 'w-40 min-w-40',
                  columnKey === 'status' && 'w-32 min-w-32',
                  columnKey === 'priority' && 'w-32 min-w-32',
                  columnKey === 'assignedTo' && 'w-40 min-w-40',
                  columnKey === 'updatedAt' && 'w-36 min-w-36',
                  columnKey === 'createdAt' && 'w-36 min-w-36'
                )}
                onClick={canSort && !loading ? () => handleSort(columnKey) : undefined}
              >
                <div className="flex items-center space-x-1">
                  <span>{column.label}</span>
                  {canSort && (
                    <div className="flex items-center space-x-1">
                      {loading && isSorted && (
                        <div className="animate-spin rounded-full h-3 w-3 border border-primary-600 border-t-transparent" />
                      )}
                      <div className="flex flex-col">
                        <HiChevronUp
                          className={clsx(
                            'w-3 h-3 -mb-1',
                            isSorted && sortConfig?.direction === 'asc'
                              ? 'text-primary-600'
                              : 'text-gray-400'
                          )}
                        />
                        <HiChevronDown
                          className={clsx(
                            'w-3 h-3',
                            isSorted && sortConfig?.direction === 'desc'
                              ? 'text-primary-600'
                              : 'text-gray-400'
                          )}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </th>
            );
          })}
        </tr>
      </thead>
    );
  };

  // Render content based on view type
  const renderContent = () => {
    if (loading && (!tickets || tickets.length === 0)) {
      return (
        <div className="flex justify-center py-12">
          <Loading size="lg" text="Loading tickets..." />
        </div>
      );
    }

    if (!tickets || tickets.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No tickets found</h3>
          <p className="text-gray-500">There are no tickets matching your current filters.</p>
        </div>
      );
    }

    // Card View with modern 2025 grid design
    if (currentPreferences.viewType === 'card') {
      return (
        <div
          className={clsx(
            // Modern responsive grid with optimal spacing
            'grid gap-8', // Increased gap for better breathing room
            'grid-cols-1', // Mobile: single column
            'sm:grid-cols-1', // Small mobile: single column
            'md:grid-cols-2', // Tablet: two columns
            'lg:grid-cols-3', // Desktop: three columns
            'xl:grid-cols-3', // Large desktop: maintain three columns for readability
            '2xl:grid-cols-4', // Ultra-wide: four columns
            // Enhanced layout properties
            'auto-rows-fr', // Equal height rows for consistency
            'place-items-stretch', // Stretch items to fill space
            // Professional spacing with 2025 standards
            'px-2', // Slight horizontal padding
            'py-4' // Vertical padding for better visual separation
          )}
          role="list"
          aria-label="Tickets card view"
        >
          {sortedTickets.map((ticket) => (
            <div key={ticket.id} role="listitem" className="h-full">
              <TicketCard
                ticket={ticket}
                selected={selectedTickets.includes(ticket.id)}
                onSelect={handleTicketToggle}
                onClick={onTicketClick}
                onStatusChange={onStatusChange}
                onPriorityChange={onPriorityChange}
                onAssignmentChange={onAssignmentChange}
                showQuickActions={showQuickActions}
                compact={currentPreferences.cardSize === 'compact'}
                showAvatar={currentPreferences.showAvatars}
                className="h-full"
              />
            </div>
          ))}
        </div>
      );
    }

    // Table View
    if (currentPreferences.viewType === 'table') {
      return (
        <div className="w-full overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
          <table className="w-full min-w-[1050px] divide-y divide-gray-200" role="table">
            {renderTableHeader()}
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedTickets.map((ticket, index) => (
                <TicketRow
                  key={ticket.id}
                  ticket={ticket}
                  selected={selectedTickets.includes(ticket.id)}
                  onSelect={handleTicketToggle}
                  onClick={onTicketClick}
                  onStatusChange={onStatusChange}
                  onPriorityChange={onPriorityChange}
                  onAssignmentChange={onAssignmentChange}
                  visibleColumns={currentPreferences.tableColumns}
                  compact={currentPreferences.tableRowHeight === 'compact'}
                  showIndex={false}
                  index={index}
                />
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // Inbox View
    if (currentPreferences.viewType === 'inbox') {
      return (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {sortedTickets.map((ticket, index) => (
            <TicketInboxRow
              key={ticket.id}
              ticket={ticket}
              selected={selectedTickets.includes(ticket.id)}
              onSelect={handleTicketToggle}
              onClick={onTicketClick}
              unread={false} // TODO: Implement unread state
              showPreview={currentPreferences.showPreviews}
              showAttachmentIndicator={true}
              threadingEnabled={false} // TODO: Implement threading
              relatedTickets={[]}
            />
          ))}
        </div>
      );
    }
    return null;
  };

  const containerClasses = clsx(
    'space-y-4',
    className
  );

  return (
    <div 
      className={containerClasses}
      data-testid={testId}
      {...props}
    >
      {/* Bulk Actions Bar */}
      {bulkActionsEnabled && selectedTickets.length > 0 && (
        <BulkActionsBar
          selectedCount={selectedTickets.length}
          totalCount={tickets?.length || 0}
          onSelectAll={handleSelectAll}
          onSelectNone={handleSelectNone}
          onBulkStatusChange={(status) => {
            if (onBulkStatusChange) {
              onBulkStatusChange(status);
            } else {
              selectedTickets.forEach(ticketId => {
                onStatusChange?.(ticketId, status);
              });
            }
          }}
          onBulkPriorityChange={(priority) => {
            if (onBulkPriorityChange) {
              onBulkPriorityChange(priority);
            } else {
              selectedTickets.forEach(ticketId => {
                onPriorityChange?.(ticketId, priority);
              });
            }
          }}
          onBulkAssign={(agentId) => {
            if (!agentId) return;
            if (onBulkAssign) {
              onBulkAssign(agentId);
            } else {
              selectedTickets.forEach(ticketId => {
                onAssignmentChange?.(ticketId, agentId);
              });
            }
          }}
          loading={loading}
          position="top"
        />
      )}

      {/* View Controls */}
      <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3">
        <div className="flex items-center space-x-4">
          {/* View Toggle */}
          <ViewToggle
            currentView={currentPreferences.viewType}
            onViewChange={handleViewChange}
            showLabels={false}
            size="sm"
          />

          {/* Ticket Count */}
          <div className="text-sm text-gray-600">
            {tickets?.length || 0} ticket{(tickets?.length || 0) !== 1 ? 's' : ''}
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {/* Column Settings - Table View Only */}
          {currentPreferences.viewType === 'table' && (
            <div className="relative" ref={columnSettingsRef}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowColumnSettings(!showColumnSettings)}
                className="flex items-center space-x-1"
              >
                <HiAdjustmentsHorizontal className="w-4 h-4" />
                <span>Columns</span>
              </Button>
              
              {showColumnSettings && (
                <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-30 min-w-48">
                  <div className="p-2">
                    <h4 className="text-xs font-medium text-gray-700 mb-2">Show Columns</h4>
                    {availableColumns.filter(col => !col.required && col.key !== 'actions').map((column) => (
                      <label
                        key={column.key}
                        className="flex items-center space-x-2 px-2 py-1 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={currentPreferences.tableColumns.includes(column.key)}
                          onChange={() => handleColumnToggle(column.key)}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700">{column.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Sort Indicator */}
          {sortConfig?.key && (
            <div className="flex items-center space-x-1 text-sm text-gray-500">
              <HiArrowUpDown className="w-4 h-4" />
              <span>
                Sorted by {getSortedColumnLabel()}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="min-h-96">
        {renderContent()}
      </div>
    </div>
  );
};
